import { useIonAlert, useIonToast } from '@ionic/react';
import type { CSSProperties } from 'react';

import {
  type CompanyDocument,
  GUIDE_PAYMENT_FAILURE,
  GuidePaymentError,
  type GuideStatus,
  type PaymentSource,
  type TaxGuide,
  isPayable,
  isUndoableByClient,
} from '~/api/documents';
import {
  GUIDE_PAYMENT_ACTION,
  type GuidePaymentAction,
  type GuidePaymentChange,
  useCanMarkGuides,
  useGuidePaymentMutation,
} from '~/api/hooks/useDocuments';
import { Icon } from '~/components/ui/Icon';
import { formatDayMonth, splitBrl } from '~/lib/format';

type GuidePayment = ReturnType<typeof useGuidePaymentMutation>;
type ToastControls = ReturnType<typeof useIonToast>;
type PresentAlert = ReturnType<typeof useIonAlert>[0];

const PIX_COPIED = 'Código Pix copiado';
const LINE_COPIED = 'Linha copiada';
const COPY_REFUSED = 'Não foi possível copiar neste aparelho.';
const AMOUNT_UNKNOWN = 'valor não informado';
const SUMMARY_SEPARATOR = ' · ';
const NOTICE_DURATION_MS = 3000;
const CONFIRM_PAYMENT_HEADER = 'Marcar como paga?';
const CONFIRM_PAYMENT_BUTTON = 'Marcar como paga';
const CANCEL_BUTTON = 'Cancelar';

const GUIDE_STATUS_LABEL: Record<GuideStatus, string> = {
  OPEN: 'em aberto',
  OVERDUE: 'vencida',
  PAID: 'paga',
  SUPERSEDED: 'substituída',
};

const GUIDE_STATUS_TAG_CLASS: Record<GuideStatus, string> = {
  OPEN: 'rx-tag rx-tag-brand',
  OVERDUE: 'rx-tag rx-tag-neg',
  PAID: 'rx-tag rx-tag-pos',
  SUPERSEDED: 'rx-tag',
};

/** A guia paga e a substituída mostram a data impressa sem dizer se ela passou: já não importa. */
const DUE_DATE_PREFIX: Record<GuideStatus, string> = {
  OPEN: 'vence em',
  OVERDUE: 'venceu em',
  PAID: 'vencimento',
  SUPERSEDED: 'vencimento',
};

const PAYMENT_SOURCE_LABEL: Record<PaymentSource, string> = {
  CLIENT: 'pelo app',
  OFFICE: 'pelo escritório',
  SERPRO: 'pela Receita',
};

const PAYMENT_NOTICE: Record<GuidePaymentAction, string> = {
  pay: 'Guia marcada como paga',
  unpay: 'Pagamento desfeito',
};

const TITLE_STYLE: CSSProperties = {
  margin: 0,
  fontSize: 15,
  fontWeight: 500,
  color: 'var(--rx-text)',
  letterSpacing: '-0.2px',
};

const AMOUNT_STYLE: CSSProperties = {
  marginTop: 8,
  fontSize: 24,
  fontWeight: 500,
  letterSpacing: '-0.5px',
  color: 'var(--rx-text)',
};

const CENTS_STYLE: CSSProperties = { fontSize: 15, color: 'var(--rx-text-2)' };

const AMOUNT_UNKNOWN_STYLE: CSSProperties = {
  marginTop: 8,
  fontSize: 14,
  color: 'var(--rx-text-2)',
};

const DUE_ROW_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 4,
  fontSize: 13,
  color: 'var(--rx-text-2)',
};

const PAYMENT_STYLE: CSSProperties = { margin: '6px 0 0', fontSize: 12, color: 'var(--rx-text-3)' };

const ACTIONS_STYLE: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 };

const ACTION_STYLE: CSSProperties = {
  flex: '1 1 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '11px 12px',
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const PRIMARY_ACTION_STYLE: CSSProperties = {
  ...ACTION_STYLE,
  flexBasis: '100%',
  background: 'var(--rx-brand)',
  border: 'none',
  color: 'var(--rx-brand-ink)',
};

const SECONDARY_ACTION_STYLE: CSSProperties = {
  ...ACTION_STYLE,
  background: 'var(--rx-surface)',
  border: '0.5px solid var(--rx-border)',
  color: 'var(--rx-text)',
};

const BAND_STYLE: CSSProperties = {
  marginTop: 16,
  padding: '14px 16px',
  background: 'var(--rx-surface)',
  border: '0.5px solid var(--rx-border)',
  borderRadius: 14,
};

/** "R$ 1.234,56", ou "valor não informado" quando o modelo não leu o valor. */
export function guideAmountLabel(guide: TaxGuide): string {
  if (!guide.amount) return AMOUNT_UNKNOWN;
  const { integer, cents } = splitBrl(guide.amount);
  return `R$ ${integer},${cents}`;
}

/** "vence em 20/10" na aberta, "venceu em 20/10" na vencida, "vencimento 20/10" nas outras. */
export function guideDueLabel(guide: TaxGuide): string {
  return `${DUE_DATE_PREFIX[guide.status]} ${formatDayMonth(guide.due_date)}`;
}

/** A linha da guia abaixo do título, no feed e nas pastas: "R$ 1.234,56 · vence em 20/10". */
export function guideSummary(guide: TaxGuide): string {
  return [guideAmountLabel(guide), guideDueLabel(guide)].join(SUMMARY_SEPARATOR);
}

/** "Paga em 21/10, pelo app": quando e por onde a guia foi marcada. */
function paymentLabel(guide: TaxGuide): string | null {
  if (!guide.paid_at || guide.paid_source === '') return null;
  return `Paga em ${formatDayMonth(guide.paid_at)}, ${PAYMENT_SOURCE_LABEL[guide.paid_source]}`;
}

function confirmPaymentMessage(companyDocument: CompanyDocument, guide: TaxGuide): string {
  return `Confirme que a guia ${companyDocument.title} (${guideAmountLabel(guide)}) já foi paga. Ela passa para "Pagas"; se for engano, use "Desfazer".`;
}

/**
 * A guia que a tela mostra: a que o "Paguei" ou o desfazer acabou de devolver, até a lista
 * chegar de novo, ou a do documento.
 */
function shownGuide(
  companyDocument: CompanyDocument,
  updated: CompanyDocument | undefined,
): TaxGuide | null {
  return updated?.id === companyDocument.id ? updated.guide : companyDocument.guide;
}

/** Um aviso por vez: o anterior sai antes, ou o Ionic ignoraria o novo. */
async function showNotice([present, dismiss]: ToastControls, message: string): Promise<void> {
  await dismiss();
  await present({ message, duration: NOTICE_DURATION_MS });
}

async function writeClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

async function copyWithNotice(text: string, copied: string, toast: ToastControls): Promise<void> {
  const written = await writeClipboard(text);
  await showNotice(toast, written ? copied : COPY_REFUSED);
}

/**
 * Pelo `mutateAsync`, e não pelos callbacks do `mutate`: na tela "Impostos" a guia paga sai da
 * lista antes de o pedido terminar, e o aviso tem de sair mesmo com o card desmontado.
 */
async function changePayment(
  payment: GuidePayment,
  change: GuidePaymentChange,
  toast: ToastControls,
): Promise<void> {
  try {
    await payment.mutateAsync(change);
    await showNotice(toast, PAYMENT_NOTICE[change.action]);
  } catch (error) {
    await showNotice(
      toast,
      error instanceof GuidePaymentError ? error.message : GUIDE_PAYMENT_FAILURE,
    );
  }
}

interface PaymentConfirmation {
  companyDocument: CompanyDocument;
  guide: TaxGuide;
  payment: GuidePayment;
  toast: ToastControls;
}

function confirmPayment(
  presentAlert: PresentAlert,
  { companyDocument, guide, payment, toast }: PaymentConfirmation,
): void {
  const change: GuidePaymentChange = {
    documentId: companyDocument.id,
    action: GUIDE_PAYMENT_ACTION.pay,
  };
  void presentAlert({
    header: CONFIRM_PAYMENT_HEADER,
    message: confirmPaymentMessage(companyDocument, guide),
    buttons: [
      { text: CANCEL_BUTTON, role: 'cancel' },
      { text: CONFIRM_PAYMENT_BUTTON, handler: () => void changePayment(payment, change, toast) },
    ],
  });
}

/** O selo da situação: "em aberto", "vencida" (em negativo), "paga" ou "substituída". */
export function GuideStatusTag({ status }: { status: GuideStatus }): JSX.Element {
  return <span className={GUIDE_STATUS_TAG_CLASS[status]}>{GUIDE_STATUS_LABEL[status]}</span>;
}

interface GuideCardProps {
  companyDocument: CompanyDocument;
  /** A empresa em operação: o "Paguei" vai com ela no `?client=`. */
  clientId: number;
  /** "Abrir": o documento no visualizador do feed. */
  onOpen(companyDocument: CompanyDocument): void;
}

/**
 * Uma guia na tela "Impostos": o título, o valor, o vencimento, o selo da situação e as ações:
 * "Copiar código Pix" (a principal), "Copiar linha digitável", "Abrir" e, com `documents` `FULL`,
 * "Paguei" ou "Desfazer". Um documento sem guia não vira card.
 */
export function GuideCard({
  companyDocument,
  clientId,
  onOpen,
}: GuideCardProps): JSX.Element | null {
  const payment = useGuidePaymentMutation(clientId);
  const guide = shownGuide(companyDocument, payment.data);
  if (!guide) return null;
  const titleId = `guia-${companyDocument.id}-titulo`;

  return (
    <article aria-labelledby={titleId} className="rx-card">
      <h2 id={titleId} style={TITLE_STYLE}>
        {companyDocument.title}
      </h2>
      <GuideFigures guide={guide} />
      <GuideActions
        companyDocument={companyDocument}
        guide={guide}
        clientId={clientId}
        payment={payment}
        onOpen={onOpen}
      />
    </article>
  );
}

interface GuideBandProps {
  companyDocument: CompanyDocument;
  clientId: number;
}

/**
 * A faixa da guia no visualizador, acima da prévia: o mesmo card sem o título (que é o da folha)
 * e sem "Abrir" (o documento já está aberto). Fora da guia, nada.
 */
export function GuideBand({ companyDocument, clientId }: GuideBandProps): JSX.Element | null {
  const payment = useGuidePaymentMutation(clientId);
  const guide = shownGuide(companyDocument, payment.data);
  if (!guide) return null;

  return (
    <section aria-label="Guia" style={BAND_STYLE}>
      <GuideFigures guide={guide} />
      <GuideActions
        companyDocument={companyDocument}
        guide={guide}
        clientId={clientId}
        payment={payment}
      />
    </section>
  );
}

function GuideFigures({ guide }: { guide: TaxGuide }): JSX.Element {
  const payment = paymentLabel(guide);

  return (
    <>
      <GuideAmount guide={guide} />
      <div style={DUE_ROW_STYLE}>
        <span>{guideDueLabel(guide)}</span>
        <GuideStatusTag status={guide.status} />
      </div>
      {payment && <p style={PAYMENT_STYLE}>{payment}</p>}
    </>
  );
}

function GuideAmount({ guide }: { guide: TaxGuide }): JSX.Element {
  if (!guide.amount) return <div style={AMOUNT_UNKNOWN_STYLE}>{AMOUNT_UNKNOWN}</div>;
  const { integer, cents } = splitBrl(guide.amount);

  return (
    <div style={AMOUNT_STYLE}>
      R$ {integer}
      <span style={CENTS_STYLE}>,{cents}</span>
    </div>
  );
}

interface GuideActionsProps {
  companyDocument: CompanyDocument;
  guide: TaxGuide;
  clientId: number;
  payment: GuidePayment;
  onOpen?(companyDocument: CompanyDocument): void;
}

/**
 * Copiar só vale para a guia que se paga: a paga e a substituída não oferecem o Pix nem a linha,
 * para ninguém pagar duas vezes nem pagar a guia trocada por uma reemissão.
 */
function GuideActions({
  companyDocument,
  guide,
  clientId,
  payment,
  onOpen,
}: GuideActionsProps): JSX.Element {
  const toast = useIonToast();
  const [presentAlert] = useIonAlert();
  const canMark = useCanMarkGuides(clientId);
  const payable = isPayable(guide);

  return (
    <div style={ACTIONS_STYLE}>
      {payable && guide.pix_code && (
        <button
          type="button"
          style={PRIMARY_ACTION_STYLE}
          onClick={() => void copyWithNotice(guide.pix_code, PIX_COPIED, toast)}
        >
          Copiar código Pix
        </button>
      )}
      {payable && guide.barcode_line && (
        <button
          type="button"
          style={SECONDARY_ACTION_STYLE}
          onClick={() => void copyWithNotice(guide.barcode_line, LINE_COPIED, toast)}
        >
          Copiar linha digitável
        </button>
      )}
      {onOpen && (
        <button
          type="button"
          style={SECONDARY_ACTION_STYLE}
          onClick={() => onOpen(companyDocument)}
        >
          <span aria-hidden="true">
            <Icon name="doc" size={14} />
          </span>
          Abrir
        </button>
      )}
      {canMark && payable && (
        <button
          type="button"
          style={SECONDARY_ACTION_STYLE}
          disabled={payment.isPending}
          onClick={() => confirmPayment(presentAlert, { companyDocument, guide, payment, toast })}
        >
          <span aria-hidden="true">
            <Icon name="check" size={14} />
          </span>
          Paguei
        </button>
      )}
      {canMark && isUndoableByClient(guide) && (
        <button
          type="button"
          style={SECONDARY_ACTION_STYLE}
          disabled={payment.isPending}
          onClick={() =>
            void changePayment(
              payment,
              { documentId: companyDocument.id, action: GUIDE_PAYMENT_ACTION.unpay },
              toast,
            )
          }
        >
          Desfazer
        </button>
      )}
    </div>
  );
}
